import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Loader2, X, ChevronDown, ChevronRight, ShieldAlert, CheckCircle2, Printer, Download, EyeOff, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';

const fmtEur = (v) => Number(v || 0).toLocaleString('it-IT', { style: 'currency', currency: 'EUR' });

const PERIODS = [
  { days: 90, label: '90 gg' },
  { days: 180, label: '180 gg' },
  { days: 365, label: '365 gg' },
  { days: 0, label: 'Tutto' },
];

const summaryText = (key, item) => {
  const eur = item.amount != null ? fmtEur(item.amount) : '';
  if (key === 'fatture_duplicate' || key === 'fatture_sospette') return `${item.supplier} — n.${item.invoice_number || ''} ${eur} × ${item.count}`;
  if (key === 'fattura_in_piu_batch') return `${item.supplier} — n.${item.invoice_number} ${eur} in ${(item.batches || []).length} batch`;
  if (key === 'bookings_duplicati') return `Prenotazione ${item.booking_code} × ${item.count}`;
  if (key === 'quote_pagamenti_doppi') return `Quote ${item.quote_id} — ${eur} × ${item.count}`;
  return `${item.beneficiary} — ${eur} × ${item.count}`;
};

const itemDetailLines = (key, item) => {
  if (item.invoices) return item.invoices.map(x => `${x.invoice_date} — n.${x.invoice_number} — ${fmtEur(x.amount)} — ${x.status}${x.paid_at ? ` il ${x.paid_at}` : ''} ${x.description || ''}`);
  if (item.batches) return item.batches.map(b => `batch del ${b.created_at} — stato ${b.status} — ${b.cbi_filename || ''}`);
  if (item.occurrences) return item.occurrences.map(o => {
    const b = o.batch || o;
    return `batch del ${b.created_at} — stato ${b.status}${o.causale ? ` — ${o.causale}` : ''}${o.beneficiary && !o.batch ? ` — ${o.beneficiary} ${fmtEur(o.amount)}` : ''}`;
  });
  if (item.guests) return [(item.guests || []).join(', ')];
  return [];
};

const buildPrintHtml = (report) => {
  const period = report.days ? `ultimi ${report.days} giorni` : 'tutto lo storico';
  let body = '';
  report.sections.forEach(sec => {
    const cls = sec.count > 0 ? 'color:#b91c1c' : 'color:#15803d';
    body += `<h3 style="${cls};border-bottom:1px solid #ddd;padding-bottom:4px;margin:18px 0 8px">${sec.title} — ${sec.count > 0 ? sec.count + ' anomalie' : 'OK'}${sec.ignored_count ? ` (${sec.ignored_count} ignorate)` : ''}</h3>`;
    sec.items.forEach(item => {
      body += `<div style="margin:8px 0;${item.ignored ? 'opacity:.55' : ''}">
        <strong>${summaryText(sec.key, item)}${item.ignored ? ' — IGNORATA' : ''}</strong>
        ${itemDetailLines(sec.key, item).map(l => `<div style="font-size:12px;color:#444;margin-left:12px">• ${l}</div>`).join('')}
      </div>`;
    });
  });
  return `<!DOCTYPE html><html><head><title>Controllo Doppioni</title></head>
  <body style="font-family:Arial,sans-serif;max-width:800px;margin:20px auto">
    <h1 style="margin-bottom:4px">Controllo Doppioni</h1>
    <p style="color:#666;margin-top:0">Generato il ${new Date(report.generated_at).toLocaleString('it-IT')} — Periodo: ${period} — Anomalie: ${report.total_findings}</p>
    ${body}
  </body></html>`;
};

const ItemDetail = ({ sectionKey, item }) => (
  <div>
    <div className="font-medium text-slate-800">{summaryText(sectionKey, item)}</div>
    {itemDetailLines(sectionKey, item).map((l, i) => (
      <div key={i} className="text-xs text-slate-600 mt-0.5">• {l}</div>
    ))}
    {item.ignored && (
      <div className="text-[11px] text-amber-600 mt-1">Ignorata da {item.dismissed_by} il {item.dismissed_at}</div>
    )}
  </div>
);

export default function DuplicatesCheckModal({ onClose, autoPrint = false }) {
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [days, setDays] = useState(365);
  const [showIgnored, setShowIgnored] = useState(false);
  const [exporting, setExporting] = useState(false);
  const printedRef = useRef(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/payment-archive/duplicates-check?days=${days}&include_ignored=${showIgnored}`);
      setReport(res.data);
      const exp = {};
      (res.data.sections || []).forEach(s => { if (s.items.length > 0) exp[s.key] = true; });
      setExpanded(exp);
    } catch {
      toast.error('Errore durante il controllo doppioni');
    } finally {
      setLoading(false);
    }
  }, [days, showIgnored]);

  useEffect(() => { load(); }, [load]);

  const doPrint = useCallback((rep) => {
    const w = window.open('', '_blank');
    if (!w) { toast.error('Popup bloccato: consenti i popup per stampare'); return; }
    w.document.write(buildPrintHtml(rep));
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 400);
  }, []);

  useEffect(() => {
    if (autoPrint && report && !printedRef.current) {
      printedRef.current = true;
      doPrint(report);
    }
  }, [autoPrint, report, doPrint]);

  const exportExcel = async () => {
    setExporting(true);
    try {
      const res = await api.get(`/payment-archive/duplicates-check/export?days=${days}`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `controllo_doppioni_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Errore export Excel');
    } finally {
      setExporting(false);
    }
  };

  const ignoreItem = async (sec, item) => {
    try {
      await api.post('/payment-archive/duplicates-check/ignore', {
        finding_id: item.finding_id, section_key: sec.key, summary: summaryText(sec.key, item),
      });
      toast.success('Segnalazione marcata come verificata: non ricomparirà');
      load();
    } catch { toast.error('Errore'); }
  };

  const unignoreItem = async (item) => {
    try {
      await api.post('/payment-archive/duplicates-check/unignore', { finding_id: item.finding_id });
      toast.success('Segnalazione ripristinata');
      load();
    } catch { toast.error('Errore'); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="duplicates-check-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[88vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-3 border-b">
          <h3 className="font-bold text-slate-900 flex items-center gap-2">
            <ShieldAlert className="h-5 w-5 text-red-600" /> Controllo Doppioni
          </h3>
          <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded" data-testid="duplicates-check-close"><X className="h-5 w-5" /></button>
        </div>
        <div className="flex items-center gap-2 px-5 py-2.5 border-b flex-wrap">
          <div className="flex rounded-lg border overflow-hidden" data-testid="dup-period-selector">
            {PERIODS.map(p => (
              <button key={p.days} onClick={() => setDays(p.days)}
                data-testid={`dup-period-${p.days}`}
                className={`px-3 py-1.5 text-xs font-medium ${days === p.days ? 'bg-red-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}>
                {p.label}
              </button>
            ))}
          </div>
          <button onClick={() => setShowIgnored(v => !v)} data-testid="dup-show-ignored"
            className={`px-3 py-1.5 text-xs font-medium rounded-lg border ${showIgnored ? 'bg-amber-100 border-amber-300 text-amber-800' : 'bg-white text-slate-600 hover:bg-slate-50'}`}>
            {showIgnored ? 'Nascondi ignorati' : `Mostra ignorati${report?.total_ignored ? ` (${report.total_ignored})` : ''}`}
          </button>
          <div className="flex-1" />
          <Button size="sm" variant="outline" onClick={() => report && doPrint(report)} disabled={!report || loading} data-testid="dup-print-btn">
            <Printer className="h-4 w-4 mr-1" /> Stampa
          </Button>
          <Button size="sm" variant="outline" onClick={exportExcel} disabled={exporting || loading} data-testid="dup-export-btn">
            {exporting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Download className="h-4 w-4 mr-1" />} Excel
          </Button>
        </div>
        <div className="overflow-y-auto p-5 space-y-3">
          {loading ? (
            <div className="flex items-center justify-center py-16 text-slate-500"><Loader2 className="h-6 w-6 animate-spin mr-2" /> Scansione in corso su fatture, batch bonifici, prenotazioni, segnalatori e preventivi…</div>
          ) : !report ? (
            <div className="text-center py-10 text-slate-500">Nessun risultato</div>
          ) : (
            <>
              <div className={`rounded-lg p-3 text-sm font-medium ${report.total_findings > 0 ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`} data-testid="duplicates-check-summary">
                {report.total_findings > 0
                  ? `⚠️ Trovate ${report.total_findings} possibili anomalie (${report.days ? `ultimi ${report.days} giorni` : 'tutto lo storico'}) — controlla le sezioni evidenziate`
                  : `✓ Nessun doppione trovato (${report.days ? `ultimi ${report.days} giorni` : 'tutto lo storico'})`}
                {report.total_ignored > 0 && !showIgnored && <span className="text-slate-500 font-normal"> — {report.total_ignored} segnalazioni ignorate</span>}
                <span className="text-slate-400 font-normal text-xs float-right" data-testid="dup-engine-version">motore {report.engine_version || 'v2'}</span>
              </div>
              {(report.sections || []).map(sec => (
                <div key={sec.key} className={`border rounded-lg ${sec.count > 0 ? 'border-red-200' : 'border-slate-200'}`} data-testid={`dup-section-${sec.key}`}>
                  <button onClick={() => setExpanded(e => ({ ...e, [sec.key]: !e[sec.key] }))}
                    className="w-full flex items-center justify-between px-4 py-2.5 text-left">
                    <span className="text-sm font-medium text-slate-800 flex items-center gap-2">
                      {sec.count > 0
                        ? <span className="inline-flex items-center justify-center min-w-[22px] h-[22px] px-1 rounded-full bg-red-600 text-white text-xs font-bold">{sec.count}</span>
                        : <CheckCircle2 className="h-4 w-4 text-green-500" />}
                      {sec.title}
                      {sec.ignored_count > 0 && <span className="text-[11px] text-amber-600">({sec.ignored_count} ignorate)</span>}
                    </span>
                    {expanded[sec.key] ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
                  </button>
                  {expanded[sec.key] && sec.items.length > 0 && (
                    <div className="px-4 pb-3 space-y-3">
                      {sec.items.map((item, i) => (
                        <div key={item.finding_id || i} className={`bg-slate-50 rounded-lg p-3 flex items-start justify-between gap-3 ${item.ignored ? 'opacity-60' : ''}`}>
                          <ItemDetail sectionKey={sec.key} item={item} />
                          {item.ignored ? (
                            <button onClick={() => unignoreItem(item)} title="Ripristina la segnalazione"
                              data-testid={`dup-unignore-${item.finding_id}`}
                              className="shrink-0 flex items-center gap-1 text-xs text-amber-700 hover:text-amber-900 border border-amber-300 rounded px-2 py-1">
                              <RotateCcw className="h-3 w-3" /> Ripristina
                            </button>
                          ) : (
                            <button onClick={() => ignoreItem(sec, item)} title="Verificato, ignora: non ricomparirà nei prossimi controlli"
                              data-testid={`dup-ignore-${item.finding_id}`}
                              className="shrink-0 flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800 border rounded px-2 py-1">
                              <EyeOff className="h-3 w-3" /> Ignora
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </>
          )}
        </div>
        <div className="px-5 py-3 border-t flex justify-end">
          <Button variant="outline" onClick={onClose} data-testid="duplicates-check-close-btn">Chiudi</Button>
        </div>
      </div>
    </div>
  );
}
